// CommitmentModalHost — FE-043c / Commitments-Modal-01.
//
// Single mount point for every Commitment modal stage. Lives at
// the dashboard layout level so any feature surface can call into
// ``useCommitmentModal`` without each surface needing to host the
// modal itself.
//
// The host renders:
//   - Quick-create + full-create modals (CommitmentCreateModal).
//   - The view modal is now the route-overlay
//     ``CommitmentDetailModal`` mounted from ``App.tsx`` —
//     openCommitmentModal forwards the call into a route-level
//     navigation with ``state.backgroundLocation`` set to the
//     current URL, so the originating page stays mounted under
//     the modal and closing the modal pops history back to it.

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CommitmentCreateModal } from "./CommitmentCreateModal";
import { useCommitmentModal } from "./CommitmentModalProvider";


export function CommitmentModalHost() {
  const modal = useCommitmentModal();
  const navigate = useNavigate();
  const location = useLocation();

  // ``openCommitmentModal`` callers (TouchpointDetailPanel,
  // future BeneAI surfaces) flip the stage to ``view``. We
  // translate that into a route-level navigation that the
  // ``CommitmentDetailModal`` overlay in ``App.tsx`` picks up.
  // The stage is then reset so the host doesn't keep firing on
  // re-renders. The current ``location`` becomes the
  // ``backgroundLocation`` so closing returns to the originator.
  useEffect(() => {
    if (modal.state.stage !== "view") return;
    if (!modal.state.commitmentId) return;
    const id = modal.state.commitmentId;
    modal.closeCommitmentModal();
    navigate(`/dashboard/commitments/${id}`, {
      state: {
        backgroundLocation: location,
        fromUrl: `${location.pathname}${location.search}`,
        ...(modal.state.returnContext.fromLabel
          ? { fromLabel: modal.state.returnContext.fromLabel }
          : {}),
      },
    });
  }, [modal, navigate, location]);

  return <CommitmentCreateModal />;
}
